import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import type { MetaPage } from '@/pages/Meta/Connection/types/meta.types';
import FunnelPageSelector from '@/pages/Meta/Flow/Funnel/components/FunnelPageSelector';

interface AddPagesDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    pages: MetaPage[];
    submittedPageIds: Set<string>;
    onSubmit: (pageIds: string[]) => void;
    isPending: boolean;
}

/**
 * Dialog secundário para adicionar novas páginas a um grupo de Utility
 * Template já existente. Aberto por cima do GroupDetailDialog — cada Dialog
 * Radix tem seu próprio Portal, então o empilhamento funciona sem ajuste de
 * z-index adicional.
 */
export default function AddPagesDialog({
    open,
    onOpenChange,
    pages,
    submittedPageIds,
    onSubmit,
    isPending,
}: AddPagesDialogProps) {
    const [selectedPageIds, setSelectedPageIds] = useState<number[]>([]);

    const handleOpenChange = (next: boolean) => {
        if (!next) setSelectedPageIds([]);
        onOpenChange(next);
    };

    const handleSubmit = () => {
        const pageIds = pages
            .filter((p) => selectedPageIds.includes(p.id))
            .map((p) => p.page_id);
        if (pageIds.length === 0) return;
        onSubmit(pageIds);
        setSelectedPageIds([]);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Adicionar páginas</DialogTitle>
                </DialogHeader>

                <div className="space-y-3">
                    <p className="text-xs text-gray-500">
                        O conteúdo original será traduzido automaticamente para o idioma de cada
                        página selecionada, igual à criação inicial.
                    </p>
                    <FunnelPageSelector selectedIds={selectedPageIds} onChange={setSelectedPageIds} />
                    {pages
                        .filter((p) => selectedPageIds.includes(p.id) && submittedPageIds.has(p.page_id))
                        .map((p) => (
                            <p key={p.id} className="text-xs text-amber-600">
                                {p.page_name} já tem esse template — será resubmetido.
                            </p>
                        ))}
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isPending}>
                        Cancelar
                    </Button>
                    <Button type="button" onClick={handleSubmit} disabled={selectedPageIds.length === 0 || isPending}>
                        {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Enviar para {selectedPageIds.length || ''} página{selectedPageIds.length > 1 ? 's' : ''}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
